import React, { useState, useEffect } from "react";
import "./Buyout.css"; // Importing the updated CSS file
import { useCart } from "../../../contexts/Cart/CartContext";
import { CartItem, generateUniqueId } from "../../../Interfaces/Types";
import { ImprovedParticipant } from "../../../Interfaces/FundraiserRole";
import config from "../../..//config";
import AddToCartButton from "../../../components/Storefront/AddToCartButton";
import { Product } from "../../../Interfaces/Product";
import fetchWithAuth from "../../../services/fetchWithAuth";
import donationImage from "../../../images/donation_box.png";
interface BuyoutComponentProps {
  originalBuyout: number;
  profitEarnedSoFar: number;
  participantID: number;
  hasBoughtout: boolean;
  fundraiserEndDate: string;
}

const BuyoutComponent: React.FC<BuyoutComponentProps> = ({
  originalBuyout,
  profitEarnedSoFar,
  participantID,
  hasBoughtout,
  fundraiserEndDate,
}) => {
  const [isTooltipVisible, setTooltipVisible] = useState(false);
  const BASE_API = config.API_BASE_URL;
  const [participantObject, setParticipantObject] =
    useState<ImprovedParticipant | null>(null);

  const { state } = useCart();

  const buyOutInCart = state.cart.some(
    (item) => item.sku === config.BUYOUT_ITEM_ID,
  );

  // TODO Update so the amount of time is configureable via API (maybe on a fundraiser by fundraiser basis)
  const hasAWeekPassed = (dateStr: string): boolean => {
    // Add timezone offset to the input string to ensure it's treated as PST
    const dateWithTimeZone = `${dateStr}T00:00:00-08:00`; // Add time and PST offset (-08:00)

    // Parse the input date string with time zone information (in PST)
    const date = new Date(dateWithTimeZone);

    // Add 2 full days to the date
    date.setDate(date.getDate() + 7);

    // Set the time to 11:59:59.999 PM PST on the same date
    date.setHours(23, 59, 59, 999);

    // Get the current time in PST
    const now = new Date();
    const pstNow = new Date(
      new Intl.DateTimeFormat("en-US", {
        timeZone: "America/Los_Angeles",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      }).format(now),
    );

    // Compare timestamps
    return pstNow.getTime() > date.getTime();
  };
  const isExpired = hasAWeekPassed(fundraiserEndDate);

  useEffect(() => {
    const fetchParticipantObject = async () => {
      try {
        const response = await fetch(
          `${BASE_API}fundraiser-role/store-info/${participantID}/`,
        );

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data: ImprovedParticipant = await response.json();

        if (!data) {
          throw new Error("API returned an empty participant object.");
        }

        setParticipantObject(data);
      } catch (error) {
        console.error("Error fetching participant data:", error);
      }
    };

    fetchParticipantObject();
  }, [BASE_API, participantID]); // Dependency array updated to include BASE_API and participantID

  const handleTooltipVisibility = (visible: boolean) => {
    setTooltipVisible(visible);
  };

  const buyoutAchieved = hasBoughtout || profitEarnedSoFar >= originalBuyout;

  const remainingBuyout = Math.max(originalBuyout - profitEarnedSoFar, 0);

  const remainingDonation =
    (originalBuyout - profitEarnedSoFar) * 0.1 +
    (originalBuyout - profitEarnedSoFar);

  const fundraiserEndedDisplay = () => {
    return (
      <div className="fundraiser-ended">
        <div className="fundraiser-ended__message">
          {buyoutAchieved ? (
            <>
              <h3 className="fundraiser-ended__title">
                🎉 Fundraiser Concluded!
              </h3>
              <p className="fundraiser-ended__text">
                The fundraiser has ended, and the donation goal was successfully
                achieved! Thank you for your support. 💖
              </p>
            </>
          ) : (
            <>
              <h3 className="fundraiser-ended__title">⚠️ Fundraiser Ended</h3>
              <p className="fundraiser-ended__text">
                Unfortunately, the fundraiser has ended, and the donation goal
                was not met. We appreciate your efforts! Please speak to your
                organization on what to do next.
              </p>
            </>
          )}
        </div>
      </div>
    );
  };
  return (
    <div className="buyout-component">
      {isExpired ? (
        fundraiserEndedDisplay()
      ) : buyoutAchieved ? (
        <div className="buyout-component__confirmation">
          <h3 className="buyout-component__title">Donation Goal Achieved</h3>
          <p className="buyout-component__detail">
            Congratulations! Your donation has been successfully reached. 🎉
          </p>
        </div>
      ) : (
        <>
          <div className="buyout-component__content-row">
            <div
              className="buyout-component__header-wrapper"
              onMouseEnter={() => handleTooltipVisibility(true)}
              onMouseLeave={() => handleTooltipVisibility(false)}
            >
              <div className="buyout-component__header">
                <h3 className="buyout-component__title">Donation Details</h3>
                <span className="buyout-component__help-icon">?</span>
              </div>
              {isTooltipVisible && (
                <div className="buyout-component__tooltip">
                  A donation is the minimum your organization needs out of your
                  participant. This amount can either be raised through
                  fundraising or paid by you directly. A 10% fee is added on top
                  of the remaining donation amount to cover processing fees. You
                  can also pay your organization the donation directly.
                </div>
              )}
            </div>

            <div className="buyout-component__details-row">
              <p className="buyout-component__detail">
                <strong>Donation Requirement:</strong> $
                {originalBuyout.toFixed(2)}
              </p>
              <p className="buyout-component__detail">
                <strong>Remaining Donation Due:</strong> $
                {remainingBuyout.toFixed(2)}
              </p>
              <p className="buyout-component__detail">
                <strong>Convenience Fee:</strong> $
                {(remainingBuyout * 0.1).toFixed(2)}
                {(remainingBuyout * 0.1).toFixed(2) === "0.00"
                  ? ""
                  : ` (10% of remaining donation)`}
              </p>
            </div>
          </div>

          <div
            className="buyout-component__button-container"
            onMouseEnter={() => handleTooltipVisibility(true)}
            onMouseLeave={() => handleTooltipVisibility(false)}
          >
            {participantObject ? (
              <AddToCartButton
                item={{
                  id: generateUniqueId(),
                  sku: config.BUYOUT_ITEM_ID,
                  name: "Fundraiser Contribution",
                  price: remainingDonation,
                  quantity: 1,
                  image: donationImage,
                }}
                participant={participantObject}
                disabled={buyOutInCart}
              />
            ) : (
              <p>Loading participant data...</p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default BuyoutComponent;
